export type Page =
  | "eventos"
  | "eventosInscripcion"
  | "cupos"
  | "alumnos"
  | "programas"
  | "novedades"
  | "colas"
  | "cursos"
  | "novedades_masivas"
  | "novedades_programadas"
  | "reservas"
  | "transacciones"
  | "estado_general";

export interface ColumnSize {
  columnTitle: string;
  columnWidth: number;
}

export interface DefaultColumnsSize {
  pages: {
    [key in Page]?: {
      [columnTitle: string]: number;
    };
  };
}

// TODO: Agregar columnas por defecto para cada página
/**
 * Columnas por defecto para cada página - minimo 150px
 * @returns Columnas por defecto para cada página
 */
export const defaultColumnsSize: DefaultColumnsSize = {
  pages: {
    eventos: {
      descripcion: 800,
      tipo: 150,
      nivel: 150,
      idTx: 150,
      componente: 150,
    },
    novedades: {
      datos_evento: 600,
      resultado: 500,
    },
    alumnos: {
      carrera: 250,
      mails: 200,
      alumno: 200,
      ipo: 100,
      inscripciones: 100,
      oferta: 100,
      online: 100,
      Select: 50,
    },
    novedades_masivas: {
      resultado: 600,
    },
  },
};

/**
 * Obtiene el ancho de las columnas de la página
 * @param page - Página en la que se encuentra el componente
 * @param defaultWidthGeneral - Ancho por defecto general
 * @param columnTitles - Títulos de las columnas
 * @returns Ancho de las columnas de la página
 */
export const getColumnWidths = (
  page: Page | undefined,
  defaultWidthGeneral: number,
  columnTitles: string[]
) => {
  if (!page) return {};

  const savedWidths = JSON.parse(localStorage.getItem("columnWidths") || "{}");
  const defaultWidths = defaultColumnsSize.pages[page] || {};

  // Crear un objeto con todos los títulos de columnas y sus anchos
  const allColumnWidths = columnTitles.reduce(
    (acc: { [key: string]: number }, title: string) => {
      // Prioridad: 1. localStorage, 2. defaultColumnsSize, 3. defaultWidthGeneral
      acc[title] =
        savedWidths[page]?.[title] ||
        defaultWidths[title] ||
        defaultWidthGeneral;
      return acc;
    },
    {}
  );

  return allColumnWidths;
};

/**
 * Obtiene el ancho de la columna por clave
 * @param page - Página en la que se encuentra el componente
 * @param columnTitle - Título de la columna
 * @param defaultWidthGeneral - Ancho por defecto general
 * @returns Ancho de la columna por clave
 */
export const getColumnWidthByKey = (
  page: Page | undefined,
  columnTitle: string,
  defaultWidthGeneral: number
) => {
  if (!page) return defaultWidthGeneral;
  // Prioridad: 1. localStorage, 2. defaultColumnsSize, 3. defaultWidthGeneral
  const savedWidths = JSON.parse(localStorage.getItem("columnWidths") || "{}");
  const defaultWidth = defaultColumnsSize.pages[page]?.[columnTitle];

  const savedWidth = savedWidths[page]?.[columnTitle];
  return savedWidth || defaultWidth || defaultWidthGeneral;
};

/**
 * Obtiene el ancho de la columna por defecto
 * @param page - Página en la que se encuentra el componente
 * @param defaultWidthGeneral - Ancho por defecto general
 * @param columnTitles - Títulos de las columnas
 * @returns Ancho de la columna por defecto
 */
export const getColumnWidthsDefault = (
  page: Page | undefined,
  defaultWidthGeneral: number,
  columnTitles: string[]
) => {
  if (!page) return {};

  const defaultWidths = defaultColumnsSize.pages[page] || {};

  // Crear un objeto con todos los títulos de columnas y sus anchos
  const allColumnWidths = columnTitles.reduce(
    (acc: { [key: string]: number }, title: string) => {
      // Prioridad: 1. defaultColumnsSize, 2. defaultWidthGeneral
      acc[title] = defaultWidths[title] || defaultWidthGeneral;
      return acc;
    },
    {}
  );

  return allColumnWidths;
};

/**
 * Obtiene el ancho de la columna por defecto
 * @param page - Página en la que se encuentra el componente
 * @param columnTitle - Título de la columna
 * @param defaultWidth - Ancho por defecto
 * @returns Ancho de la columna por defecto
 */
export const getDefaultColumnWidth = (
  page: Page | undefined,
  columnTitle: string,
  defaultWidth: number
) => {
  if (!page) return defaultWidth;

  return defaultColumnsSize.pages[page]?.[columnTitle] || defaultWidth;
};

export const saveColumnWidth = (
  page: Page | undefined,
  columnTitle: string,
  width: number
) => {
  if (!page) return;

  const savedWidths = JSON.parse(localStorage.getItem("columnWidths") || "{}");
  savedWidths[page] = savedWidths[page] || {};
  savedWidths[page][columnTitle] = width;
  localStorage.setItem("columnWidths", JSON.stringify(savedWidths));
};

export const getVisibleColumns = (page: Page, defaultVisibleColumns?: { [key: string]: boolean }): { [key: string]: boolean } => {
  const stored = localStorage.getItem(`tableList_visibleColumns_${page}`);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error parsing stored visible columns:', error);
    }
  }
  return defaultVisibleColumns || {};
};

export const saveVisibleColumns = (page: Page, visibleColumns: { [key: string]: boolean }) => {
  localStorage.setItem(`tableList_visibleColumns_${page}`, JSON.stringify(visibleColumns));
};
